export type LengthUnit = 'm' | 'ft';

/**
 * Determine a point cloud's length unit from its proj4 projection string — the
 * authoritative source (the same string already stored on LoadablePointCloud).
 *
 *   +units=us-ft / +units=ft   -> 'ft'
 *   +units=m                   -> 'm'
 *   +to_meter=0.3048…          -> 'ft'  (1 unit ≈ 0.3048 m = 1 foot)
 *   +to_meter=1                -> 'm'   (1 unit = 1 metre)
 *
 * Returns null when the projection is missing or the unit cannot be read, so the
 * caller can fall back to its own default instead of guessing wrong.
 */
export function lengthUnitFromProjection(
    projection?: string | null
): LengthUnit | null {
    if (!projection) return null;
    const proj = projection.toLowerCase();

    // Explicit +units= wins when present.
    const unitsMatch = proj.match(/\+units=([a-z-]+)/);
    if (unitsMatch) {
        const u = unitsMatch[1];
        if (u === 'm') return 'm';
        if (u === 'ft' || u === 'us-ft' || u === 'ft-i' || u === 'us-ft-i') {
            return 'ft';
        }
    }

    // Otherwise infer from +to_meter (metres-per-unit): ~0.3048 = foot, ~1 = metre.
    const toMeterMatch = proj.match(/\+to_meter=([0-9.]+)/);
    if (toMeterMatch) {
        const toMeter = parseFloat(toMeterMatch[1]);
        if (Number.isFinite(toMeter) && toMeter > 0) {
            return Math.abs(toMeter - 0.3048) < Math.abs(toMeter - 1) ? 'ft' : 'm';
        }
    }

    return null;
}
